import * as React from 'react'

import Konva from 'konva'
import { KonvaEventObject } from 'konva/lib/Node'
import { observer } from 'mobx-react'
import { Circle, Group, Line, Path, Rect, Text } from 'react-konva'

import { DEFAULT_GLYPH_STROKE_WIDTH } from '~/client/src/shared/components/SitemapHelpers/utils/iconGeometry'
import { ASSIGNED_STAR_FILL } from '~/client/src/shared/constants/workflowStatusColors'
import {
  ASSIGNED_STAR_CENTER,
  ASSIGNED_STAR_SPEC,
  MAP_SHADOW_LAYERS,
  PIN_PATH_DATA,
  PIN_SELECTED_SCALE,
  PIN_SPEC,
} from '~/client/src/shared/map/markerGeometry'

import ThemeMode from '../../utils/ThemeModeManager'

import Colors from '~/client/src/shared/theme.module.scss'

const { getHEXColor } = ThemeMode

// The path is drawn unscaled, so every offset below is in viewBox units.
const PIN_UNITS_W = PIN_SPEC.viewBoxWidth
const PIN_UNITS_H = PIN_SPEC.viewBoxHeight

// Carried forward verbatim rather than re-derived from MARKER_CLEARANCE_PX: it is the anchor,
// and today's placement above the location marker is calibrated against it.
const PIN_LIFT_UNITS = PIN_UNITS_H * 1.5

// The glyph box, in pin units — the same 24 the raster and the DOM pin use. Fitted here, not
// by a caller: an offset picked for one body size is what broke when the art went 20x29 -> 35x49.
const GLYPH_UNITS = 24

// A badge, so it holds its size and is drawn 1:1 in pin units like the raster's.
const STAR_ORIGIN = {
  x: ASSIGNED_STAR_CENTER.x - ASSIGNED_STAR_SPEC.sizePx / 2,
  y: ASSIGNED_STAR_CENTER.y - ASSIGNED_STAR_SPEC.sizePx / 2,
}

// A konva shape casts one shadow, so the spec's two-layer stack collapses to its dominant
// layer — still the spec's numbers, not the invented centred blur this pin used to draw.
const [{ offsetYPx: SHADOW_OFFSET_Y, blurPx: SHADOW_BLUR }] = MAP_SHADOW_LAYERS
const SHADOW_OPACITY = 0.1
const SHADOW_COLOR = '#000000'

// Across the head rather than [0, 20, 20, 0], which spanned the whole of the old 20x30 body
// and lands in the corner of a 35x49 one.
const SLASH_REACH = PIN_SPEC.headRadius / Math.SQRT2
const PIN_SLASH_POINTS = [
  PIN_UNITS_W / 2 - SLASH_REACH,
  PIN_SPEC.glyphCenterY + SLASH_REACH,
  PIN_UNITS_W / 2 + SLASH_REACH,
  PIN_SPEC.glyphCenterY - SLASH_REACH,
]

const PIN_DEFAULT_SCALE = 1
const TEXT_FONT_SIZE = 14
// The count holder's centre, in pin units. Derived, because the offsets here used to be bare
// numbers that encoded half the OLD 20-wide body — they stopped meaning "centred" the moment
// the art became 35 wide, and the digit drifted the other way because its own math did read
// the new width.
const HOLDER_CENTER = { x: PIN_UNITS_W / 2, y: -10 }
const HOLDER_RADIUS = 10
const HOLDER_STROKE_WIDTH = 1
const ELLIPSE_HEIGHT = 15
const ELLIPSE_WIDTH = 11

interface IProps {
  text: string
  // Resolved by the caller, never re-derived here. One shared resolver exists so that no two
  // renderers disagree about what a status looks like, and a pin that reads flags to pick its
  // own colour is exactly where a second opinion creeps back in.
  fill: string
  // The glyph, its stroke, and the count text: whatever reads legibly on `fill`.
  ink: string
  // One path string per element of the mark, from the shared icon registry.
  glyph: string[]
  isAssigned?: boolean
  isSelected: boolean
  isCanceled?: boolean
  shouldRenderCircle?: boolean
  x: number
  y: number

  onClick: (event: KonvaEventObject<MouseEvent>) => void
  onTouchEnd: (event: KonvaEventObject<TouchEvent>) => void
}

// Every pin on a whiteboard sitemap: the body, its count holder, the glyph and the assigned
// star. What kind of thing it represents lives entirely in the props — a workflow's status
// colour and a delivery's are both just `fill`.
@observer
export default class KonvaPin extends React.Component<
  IProps & Konva.NodeConfig
> {
  private text: Konva.Text = null

  public componentDidMount() {
    this.setTextBoxSizes()
  }

  public componentDidUpdate() {
    this.setTextBoxSizes()
  }

  public render() {
    const {
      text,
      fill,
      ink,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      glyph,
      isAssigned,
      isSelected,
      isCanceled,
      shouldRenderCircle: shouldRenderHolder,
      ...rest
    } = this.props

    const shouldRenderEllipse = shouldRenderHolder && text?.length > 1
    const shouldRenderCircle = shouldRenderHolder && text?.length < 2

    return (
      <Group
        {...rest}
        scaleX={isSelected ? PIN_SELECTED_SCALE : PIN_DEFAULT_SCALE}
        scaleY={isSelected ? PIN_SELECTED_SCALE : PIN_DEFAULT_SCALE}
        offsetX={PIN_UNITS_W / 2}
        offsetY={PIN_LIFT_UNITS}
        // Load-bearing, not decoration: BaseMapView re-applies PIN_SELECTED_SCALE off this
        // attr every time it counter-scales the stage for zoom.
        isSelected={isSelected}
      >
        {isCanceled && (
          <Line
            points={PIN_SLASH_POINTS}
            strokeWidth={HOLDER_STROKE_WIDTH}
            stroke={getHEXColor(Colors.mapWhite)}
          />
        )}
        {shouldRenderCircle && (
          <Circle
            radius={HOLDER_RADIUS}
            fill={fill}
            stroke={getHEXColor(Colors.mapWhite)}
            strokeWidth={HOLDER_STROKE_WIDTH}
            shadowColor={SHADOW_COLOR}
            shadowBlur={SHADOW_BLUR}
            shadowOffsetY={SHADOW_OFFSET_Y}
            shadowOpacity={SHADOW_OPACITY}
            offsetX={-HOLDER_CENTER.x}
            offsetY={-HOLDER_CENTER.y}
          />
        )}
        {shouldRenderEllipse && (
          <Rect
            fill={fill}
            stroke={getHEXColor(Colors.mapWhite)}
            strokeWidth={HOLDER_STROKE_WIDTH}
            shadowColor={SHADOW_COLOR}
            shadowBlur={SHADOW_BLUR}
            shadowOffsetY={SHADOW_OFFSET_Y}
            shadowOpacity={SHADOW_OPACITY}
            offsetY={ELLIPSE_HEIGHT / 2 - HOLDER_CENTER.y}
            height={ELLIPSE_HEIGHT}
            width={ELLIPSE_WIDTH * text.length}
            cornerRadius={HOLDER_RADIUS}
            offsetX={(ELLIPSE_WIDTH * text.length) / 2 - HOLDER_CENTER.x}
          />
        )}
        {text && shouldRenderHolder && (
          <Text
            ref={this.setText}
            text={text}
            fontSize={TEXT_FONT_SIZE}
            fontStyle="bold"
            fill={ink}
          />
        )}
        <Path
          data={PIN_PATH_DATA}
          fill={fill}
          stroke={getHEXColor(Colors.mapWhite)}
          strokeWidth={PIN_SPEC.strokePx}
          shadowColor={SHADOW_COLOR}
          shadowBlur={SHADOW_BLUR}
          shadowOffsetY={SHADOW_OFFSET_Y}
          shadowOpacity={SHADOW_OPACITY}
        />
        {this.glyphPaths}
        {isAssigned && (
          <Path
            data={ASSIGNED_STAR_SPEC.path}
            fill={ASSIGNED_STAR_FILL}
            stroke={getHEXColor(Colors.mapWhite)}
            strokeWidth={ASSIGNED_STAR_SPEC.strokePx}
            x={STAR_ORIGIN.x}
            y={STAR_ORIGIN.y}
          />
        )}
      </Group>
    )
  }

  // Every mark is authored on the same 24 grid the glyph box uses, so the fit is a plain
  // centring — no per-entry viewBox, and nothing filled to pick a fill rule for.
  private get glyphPaths(): JSX.Element[] {
    const { glyph, ink } = this.props
    const origin = PIN_UNITS_W / 2 - GLYPH_UNITS / 2

    return glyph.map((data, index) => (
      <Path
        key={index}
        data={data}
        stroke={ink}
        strokeWidth={DEFAULT_GLYPH_STROKE_WIDTH}
        lineCap="round"
        lineJoin="round"
        x={origin}
        y={PIN_SPEC.glyphCenterY - GLYPH_UNITS / 2}
      />
    ))
  }

  private setText = (text: Konva.Text) => {
    this.text = text
  }

  private setTextBoxSizes() {
    if (!this.text) {
      return
    }

    // Centred on the holder rather than on a half-body expression: the two used to be derived
    // from different widths, so the digit and its disc drifted apart.
    this.text.offsetX(this.text.width() / 2 - HOLDER_CENTER.x)
    this.text.offsetY(this.text.height() / 2 - HOLDER_CENTER.y)
  }
}
